/**
 * @flow
 */

import React, { useEffect, useCallback, useMemo } from 'react';
import gql from 'graphql-tag';
import { enableExperimentalFragmentVariables } from 'graphql-tag';
import { useQuery, useApolloClient } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Header } from 'semantic-ui-react';
import { format, isAfter, startOfDay } from 'date-fns';
import {
    get,
    find,
    some,
    cloneDeep,
    remove,
    findIndex,
    each,
    filter,
    isEmpty,
    uniqBy,
    property,
} from 'lodash';

import { setSettings } from '../../../../actions/salon';
import Timetable from './Timetable';
import AppointmentCell from './AppointmentCell';
import AppointmentCardContainer from './AppointmentCardContainer';
import AppointmentColumn from '../../containers/timetable/AppointmentColumn';
import { Header as TableHeader } from '../../../../components/timetable';
import { MasterTypeIcon } from '../../../../components';
import { renderCards } from './MasterTable';

import {
    APPOINTMENT_FRAGMENT,
    APPOINTMENT_ADD_SUBSCRIPTION,
    APPOINTMENT_DELETE_SUBSCRIPTION,
} from '../../containers/queries';

import { MASTER_CABINET_TABLE_FRAGMENT } from '../../containers/masterAccessQueries';

const intlMessages = defineMessages({
    noSchedule: {
        id: 'pages.appointments.timetable.cabinets.noShedule',
        defaultMessage: 'No cabinet added',
    },
});

const NoScheduleMessage = () => (
    <div>
        <FormattedMessage
            id="pages.appointments.timetable.cabinets.noShedule"
            defaultMessage="No cabinet added"
        />
        <Header.Subheader>
            <FormattedMessage
                id="pages.appointments.timetable.cabinet.scheduleIntructions"
                defaultMessage={`To add an cabinet, go to "Settings → Cabinets"`}
            />
        </Header.Subheader>
    </div>
);

// The query to get current salon object for cabinets
export const GET_SALON_QUERY = gql`
    query getSalon(
        $date: String!
        $salon: ID!
        $showArchive: Boolean
        $lang: String!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    name
                    administrator {
                        id
                    }
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                        enableArchive
                    }
                    cabinets(date: $date) {
                        id
                        name(lang: $lang)
                        schedule {
                            startAt
                            endAt
                        }
                        appointments(date: $date, showArchive: $showArchive) {
                            id
                            ...appointment
                        }
                    }
                }
            }
            ...onMasterCabinetTable
        }
    }

    ${APPOINTMENT_FRAGMENT}
    ${MASTER_CABINET_TABLE_FRAGMENT}
`;

const CabinetTable = () => {
    const apolloClient = useApolloClient();
    const dispatch = useDispatch();

    const userId = useSelector(({ user }) => user.get('id'));
    const date = useSelector(({ appointments }) => appointments.get('date'));
    const salonId = useSelector(({ user }) => user.get('salon'));
    const showArchive = useSelector(({ salon }) => salon.get('showArchive'));
    const currentLanguage = useSelector(({ intl }) => intl.get('locale') || intl.get('defaultLanguage'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const isSysadmin = useSelector(({ user }) => user.get('isSysadmin'));
    const allowToAddAppointments =
        isOwner || !isAfter(startOfDay(new Date()), startOfDay(new Date(date)));

    const queryVariables = {
        date: format(new Date(date), 'yyyy-MM-dd'),
        salon: salonId,
        showArchive,
        lang: currentLanguage,
    };

    const { data, loading, subscribeToMore } = useQuery(GET_SALON_QUERY, {
        variables: queryVariables,
        fetchPolicy: 'cache-and-network',
        skip: !salonId,
    });

    const salon = get(data, 'viewer.salon');
    const cabinets = useMemo(
        () => filter(get(salon, 'cabinets'), cabinet => !!cabinet.schedule.length),
        [salon],
    );

    // Side effect: setSettings in sysadmin mode
    useEffect(() => {
        if (
            !loading &&
            salon &&
            isSysadmin &&
            !isEmpty(salon) &&
            salon.administrator.id !== userId
        ) {
            const salonSettings = Object.assign(
                { enableArchive: get(salon, 'settings.enableArchive') },
                get(salon, 'settings.schedule'),
            );
            dispatch(setSettings(salonSettings));
        }
    }, [loading, salon, isSysadmin, userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Subscriptions
    useEffect(() => {
        if (!salonId) return;
        const dateStr = format(new Date(date), 'yyyy-MM-dd');

        const unsubAdd = subscribeToMore({
            document: APPOINTMENT_ADD_SUBSCRIPTION,
            variables: {
                salon: salonId,
                date: dateStr,
                lang: currentLanguage,
            },
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) {
                    return prev;
                }

                const next = cloneDeep(prev);
                const appointment = subscriptionData.data.appointmentAdded;

                if (
                    (showArchive || !appointment.archived) &&
                    appointment.cabinet &&
                    some(next.viewer.salon.cabinets, {
                        id: appointment.cabinet.id,
                    })
                ) {
                    each(next.viewer.salon.cabinets, (cabinet, key) => {
                        const index = findIndex(cabinet.appointments, [
                            'id',
                            appointment.id,
                        ]);
                        if (index > -1) {
                            if (appointment.cabinet.id == cabinet.id) {
                                next.viewer.salon.cabinets[key].appointments[
                                    index
                                ] = appointment;
                            } else {
                                remove(
                                    next.viewer.salon.cabinets[key].appointments,
                                    ['id', appointment.id],
                                );
                            }
                        } else {
                            if (appointment.cabinet.id == cabinet.id) {
                                next.viewer.salon.cabinets[
                                    key
                                ].appointments.push(appointment);
                            }
                        }
                    });
                }

                return next;
            },
        });

        const unsubDelete = subscribeToMore({
            document: APPOINTMENT_DELETE_SUBSCRIPTION,
            variables: {
                salon: salonId,
                date: dateStr,
                lang: currentLanguage,
            },
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) {
                    return prev;
                }

                const next = cloneDeep(prev);
                const appointment = subscriptionData.data.appointmentDeleted;

                let cabinet = null;
                // check if appointment has cabinet (related to #312)
                if (appointment.cabinet) {
                    cabinet = find(next.viewer.salon.cabinets, {
                        id: appointment.cabinet.id,
                    });
                }

                if (cabinet) {
                    remove(cabinet.appointments, o => o.id === appointment.id);
                }

                return next;
            },
        });

        return () => {
            unsubAdd();
            unsubDelete();
        };
    }, [salonId, date, showArchive, currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateAppointmentClient = useCallback(
        client => {
            const variables = {
                date: format(new Date(date), 'yyyy-MM-dd'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_SALON_QUERY,
                variables,
            });
            const cabinetNodes = cacheData.viewer.salon.cabinets || [];
            const nextCabinets = cabinetNodes.map(cabinet => {
                const appointments = cabinet.appointments || [];
                const nextAppointments = appointments.map(appointment => {
                    if (appointment.client.id === client.id) {
                        return {
                            ...appointment,
                            notes: client.notes,
                        };
                    }
                    return appointment;
                });
                return {
                    ...cabinet,
                    appointments: nextAppointments,
                };
            });
            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        cabinets: nextCabinets,
                    },
                },
            };
            apolloClient.cache.writeQuery({
                query: GET_SALON_QUERY,
                variables,
                data: nextData,
            });
        },
        [date, salonId, showArchive, currentLanguage, apolloClient],
    );

    const createAppointments = useCallback(
        appointments => {
            const variables = {
                date: format(new Date(date), 'yyyy-MM-dd'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_SALON_QUERY,
                variables,
            });

            const cabinetNodes = cacheData.viewer.salon.cabinets || [];
            const nextCabinets = cabinetNodes.map(cabinet => {
                const newCabinetAppointments = appointments
                    .filter(app => app.cabinet && app.cabinet.id === cabinet.id)
                    .map(app => ({
                        ...app,
                        invoice: null,
                    }));
                if (newCabinetAppointments.length === 0) {
                    return cabinet;
                }
                return {
                    ...cabinet,
                    appointments: [
                        ...(cabinet.appointments || []),
                        ...newCabinetAppointments,
                    ],
                };
            });
            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        cabinets: nextCabinets,
                    },
                },
            };

            apolloClient.cache.writeQuery({
                query: GET_SALON_QUERY,
                variables,
                data: nextData,
            });
        },
        [date, salonId, showArchive, currentLanguage, apolloClient],
    );

    const archiveAppointment = useCallback(
        appointment => {
            const variables = {
                salon: salonId,
                showArchive,
                date: format(new Date(date), 'yyyy-MM-dd'),
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_SALON_QUERY,
                variables,
            });
            const cabinetNodes = cacheData.viewer.salon.cabinets || [];
            const nextCabinets = cabinetNodes.map(cabinet => {
                if (cabinet.id !== appointment.cabinet.id) {
                    return cabinet;
                }
                return {
                    ...cabinet,
                    appointments: (cabinet.appointments || []).filter(
                        item => item.id !== appointment.id,
                    ),
                };
            });
            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        cabinets: nextCabinets,
                    },
                },
            };
            apolloClient.cache.writeQuery({
                query: GET_SALON_QUERY,
                variables,
                data: nextData,
            });
        },
        [date, salonId, showArchive, currentLanguage, apolloClient],
    );

    if (!salon && loading) {
        return <Loader size="big" inline="centered" active />;
    }
    if (!salon) {
        return null;
    }

    return (
        <Timetable
            salon={salon}
            loading={loading}
            date={date}
            renderColumn={columnProps => (
                <AppointmentColumn
                    {...columnProps}
                    updateAppointment={undefined}
                    inCabinet
                />
            )}
            noScheduleMessage={NoScheduleMessage}
            renderCell={cellProps => (
                <AppointmentCell
                    key={cellProps.time}
                    salon={salon}
                    date={date}
                    allowToAddAppointments={allowToAddAppointments}
                    createAppointments={createAppointments}
                    {...cellProps}
                />
            )}
            renderCards={({ provider, inCabinet }) =>
                renderCards({
                    provider,
                    inCabinet,
                    salon,
                    updateAppointmentClient,
                    archiveAppointment,
                })
            }
            renderHeader={({ provider }) => (
                <TableHeader provider={provider} headerText={provider.name} />
            )}
            providers={cabinets}
            allowToAddAppointments={allowToAddAppointments}
            createAppointments={createAppointments}
        />
    );
};

export default CabinetTable;
